import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import {
  createLoanRepurchase,
  createLoanSaleTrade,
  deleteLoanRepurchase,
  deleteLoanSaleTrade,
  getLoanSalesTrades,
  getLoanSubledger,
  updateLoanRepurchase,
  updateLoanSaleTrade,
  updateRepurchaseReserve,
} from 'services'
import { svgLoading } from 'stories/assets'
import { confirm, thousandSeperator } from 'utils'

import { EditLoanRepurchaseDialog } from './EditLoanRepurchaseDialog'
import { EditLoanSaleTradeDialog } from './EditLoanSaleTradeDialog'
import type { ILoanRepurchase, ILoanSaleTrade, IRepurchaseReserve } from './types'

const formatAmount = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `$${thousandSeperator(value)}`
}

export const LoanSalesTrades = () => {
  const [isLoading, setLoading] = useState(false)
  const [trades, setTrades] = useState<ILoanSaleTrade[]>([])
  const [tradeTotals, setTradeTotals] = useState<Record<string, number>>({})
  const [repurchases, setRepurchases] = useState<ILoanRepurchase[]>([])
  const [reserve, setReserve] = useState<IRepurchaseReserve | null>(null)
  const [loanOptions, setLoanOptions] = useState<Record<string, string>>({})
  const [editTrade, setEditTrade] = useState<Partial<ILoanSaleTrade> | null>(null)
  const [editRepurchase, setEditRepurchase] = useState<Partial<ILoanRepurchase> | null>(null)
  const [reserveDraft, setReserveDraft] = useState('')
  const [savingReserve, setSavingReserve] = useState(false)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const [result, subledger] = await Promise.all([getLoanSalesTrades(), getLoanSubledger()])
      setTrades(result.trades?.results || [])
      setTradeTotals(result.trades?.totals || {})
      setRepurchases(result.repurchases?.results || [])
      setReserve(result.reserve || null)
      setReserveDraft(String(result.reserve?.reserveAmount ?? ''))
      setLoanOptions(
        (subledger.results || []).reduce((acc: Record<string, string>, row: { loanId: string }) => {
          if (row.loanId) acc[row.loanId] = row.loanId
          return acc
        }, {}),
      )
    } catch (error) {
      console.error(error)
      setTrades([])
      setRepurchases([])
    }
    setLoading(false)
  }

  const onSaveTrade = async (data: Partial<ILoanSaleTrade>) => {
    if (editTrade?.id) await updateLoanSaleTrade(editTrade.id, data)
    else await createLoanSaleTrade(data)
    setEditTrade(null)
    fetch()
  }

  const onSaveRepurchase = async (data: Partial<ILoanRepurchase>) => {
    if (editRepurchase?.id) await updateLoanRepurchase(editRepurchase.id, data)
    else await createLoanRepurchase(data)
    setEditRepurchase(null)
    fetch()
  }

  const onDeleteTrade = async (item: ILoanSaleTrade) => {
    const ok = await confirm(`Delete trade for loan "${item.loanId}"?`)
    if (!ok) return
    setLoading(true)
    await deleteLoanSaleTrade(item.id)
    fetch()
  }

  const onDeleteRepurchase = async (item: ILoanRepurchase) => {
    const ok = await confirm(`Delete repurchase for loan "${item.loanId}"?`)
    if (!ok) return
    setLoading(true)
    await deleteLoanRepurchase(item.id)
    fetch()
  }

  const onSaveReserve = async () => {
    setSavingReserve(true)
    try {
      await updateRepurchaseReserve({ reserveAmount: Number(reserveDraft || 0) })
      fetch()
    } finally {
      setSavingReserve(false)
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Loan Sales &amp; Trades
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Basis and price pull from the Loan Subledger. Realized Gain = Sale Price + SRP + Gross MSR Cap. − Loan Basis
        (MSR Cap. from Servicing & MSR capitalized amount at sale; ties to GL 4010). Open Receivable (unsettled)
        ties to GL 1300.
      </p>

      <div className="mb-8">
        <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
          <h2 className="text-lg font-semibold">Loan Sale / Trade</h2>
          <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditTrade({})}>
            <PlusIcon className="w-4 h-4" /> Add
          </button>
        </div>

        <div className="relative overflow-x-auto">
          <LayoutLoading show={isLoading} />
          <table className="w-full text-sm text-left text-gray-700 min-w-max">
            <thead className="text-xs uppercase bg-gray-100">
              <tr>
                {[
                  'Loan ID',
                  'Investor',
                  'Trade Type',
                  'Settled?',
                  'Loan Basis',
                  'Sale Price',
                  'SRP',
                  'MSR Cap.',
                  'Realized Gain',
                  'Open Receivable',
                  'Action',
                ].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                    No loan sales or trades yet.
                  </td>
                </tr>
              )}
              {trades.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{item.loanId}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.investor || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.tradeType}</td>
                  <td className="px-3 py-2">{item.settled ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.loanBasis)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.salePrice)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.srp)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.msrCap)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.realizedGain)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.openReceivable)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-center">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => setEditTrade(item)}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDeleteTrade(item)}>
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {trades.length > 0 && (
              <tfoot>
                <tr className="bg-blue-50 font-semibold border-t-2">
                  <td colSpan={8} className="px-3 py-2">
                    TOTAL
                  </td>
                  <td className="px-3 py-2 text-right">{formatAmount(tradeTotals.realizedGain)}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(tradeTotals.openReceivable)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
          <h2 className="text-lg font-semibold">Repurchases &amp; Reserves</h2>
          <button className="btn-icon flex items-center gap-1 text-sm" onClick={() => setEditRepurchase({})}>
            <PlusIcon className="w-4 h-4" /> Add Repurchase
          </button>
        </div>

        <div className="relative overflow-x-auto mb-6">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="text-xs uppercase bg-gray-100">
              <tr>
                {['Loan', 'Repurchase Price', 'Reason', 'Action'].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {repurchases.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                    No repurchases yet.
                  </td>
                </tr>
              )}
              {repurchases.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{item.loanId}</td>
                  <td className="px-3 py-2 text-right">{formatAmount(item.repurchasePrice)}</td>
                  <td className="px-3 py-2">{item.reason || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2 items-center">
                      <span
                        className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                        onClick={() => setEditRepurchase(item)}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                      <span
                        className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                        onClick={() => onDeleteRepurchase(item)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-gray-200 rounded p-4 bg-gray-50 space-y-3 max-w-xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-800 shrink-0">Repurchase / indemnification reserve</span>
            <input
              className="border border-gray-300 rounded px-2 py-1.5 text-sm w-40"
              type="number"
              value={reserveDraft}
              onChange={(e) => setReserveDraft(e.target.value)}
            />
            <button
              className="bg-primary text-white rounded px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={savingReserve}
              onClick={onSaveReserve}
            >
              Save
            </button>
          </div>
          <div className="text-sm text-gray-700 flex flex-wrap gap-x-8 gap-y-1">
            <span>
              GL {reserve?.glAccountNo || '2220'} balance:{' '}
              <strong>{formatAmount(reserve?.glBalance)}</strong>
            </span>
            <span>
              Reserve ties to GL?:{' '}
              <strong className={reserve?.ties ? 'text-green-700' : 'text-red-600'}>
                {reserve?.ties ? 'TIES' : 'VARIANCE'}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {editTrade && (
        <EditLoanSaleTradeDialog
          item={editTrade}
          loanOptions={loanOptions}
          onClose={() => setEditTrade(null)}
          onSave={onSaveTrade}
        />
      )}
      {editRepurchase && (
        <EditLoanRepurchaseDialog
          item={editRepurchase}
          loanOptions={loanOptions}
          onClose={() => setEditRepurchase(null)}
          onSave={onSaveRepurchase}
        />
      )}
    </div>
  )
}
